if (!customElements.get('cosmogen-certificate')) {
  customElements.define(
    'cosmogen-certificate',
    class CosmogenCertificate extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.codeInput = this.querySelector('#CosmogenCertificateForm-code');
        this.errorContainer = this.querySelector('#CosmogenCertificateForm-error');
        this.errorMessage = this.querySelector('.cosmogen-certificate__error-message');
        this.submitButton = this.querySelector('button[type="submit"]');
        this.buttonText = this.querySelector('.cosmogen-certificate__button-text');
        this.buttonLoading = this.querySelector('.cosmogen-certificate__button-loading');

        this.share = this.querySelector('.cosmogen-certificate__share');
        this.shareCode = this.querySelector('.cosmogen-certificate__share-code');
        this.copyButton = this.querySelector('[data-action="copy"]');
        this.shareButton = this.querySelector('[data-action="share"]');

        this.serviceUrl = (this.dataset.serviceUrl || 'https://certificate.cosmogendiamonds.com').replace(/\/+$/, '');
        this.pageUrl = this.dataset.pageUrl || window.location.pathname;
        this.lastPdf = null;

        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.codeInput.addEventListener('input', this.onInput.bind(this));
        this.copyButton?.addEventListener('click', this.onCopy.bind(this));
        this.shareButton?.addEventListener('click', this.onShare.bind(this));

        if (this.shareButton && !navigator.share) this.shareButton.hidden = true;

        // Ссылка с кодом: подставляем его и сразу показываем кнопки.
        // Скачивание не запускаем: на телефонах и в мессенджерах браузер
        // блокирует загрузку без действия пользователя.
        const code = new URLSearchParams(window.location.search).get('code');
        if (code && code.trim()) {
          this.codeInput.value = code.trim();
          this.showShare(code.trim());
        }
      }

      onInput() {
        this.hideError();
      }

      pdfUrl(code) {
        return `${this.serviceUrl}/${encodeURIComponent(code)}.pdf`;
      }

      shareUrl(code) {
        const url = new URL(this.pageUrl, window.location.origin);
        url.search = '';
        url.searchParams.set('code', code);
        return url.toString();
      }

      async onSubmit(event) {
        event.preventDefault();

        const code = this.codeInput.value.trim();

        if (!code) {
          this.showError(this.dataset.errorEmpty);
          this.codeInput.focus();
          return;
        }

        this.hideError();
        this.setLoading(true);

        try {
          const response = await fetch(this.pdfUrl(code), { headers: { Accept: 'application/pdf' } });

          if (response.status === 404) throw new Error('not_found');
          if (response.status === 429) throw new Error('busy');
          if (!response.ok) throw new Error('download');

          const blob = await response.blob();
          if (blob.size < 500) throw new Error('download');

          const pdfBlob = new Blob([blob], { type: 'application/pdf' });
          this.lastPdf = { code, blob: pdfBlob };

          const blobUrl = URL.createObjectURL(pdfBlob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = `CosmoGen_Certificate_${code}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => URL.revokeObjectURL(blobUrl), 100);

          this.showShare(code);
          this.updateAddress(code);
          window.cosmogenAnalytics?.track('certificate_check', { result: 'success' });
        } catch (error) {
          const messages = {
            not_found: this.dataset.errorNotFound,
            busy: this.dataset.errorBusy,
          };
          this.showError(messages[error.message] || this.dataset.errorDownload || 'Download failed. Please try again.');
          this.hideShare();
          window.cosmogenAnalytics?.track('certificate_check', { result: error.message === 'not_found' ? 'not_found' : 'error' });
        } finally {
          this.setLoading(false);
        }
      }

      showShare(code) {
        if (!this.share) return;
        this.share.hidden = false;
        if (this.shareCode) this.shareCode.textContent = code;
        this.share.dataset.code = code;
      }

      hideShare() {
        if (this.share) this.share.hidden = true;
      }

      /** Адрес страницы становится ссылкой на код: её можно скопировать из адресной строки. */
      updateAddress(code) {
        try {
          window.history.replaceState(null, '', this.shareUrl(code));
        } catch (error) {
          /* history недоступна — не страшно */
        }
      }

      async onCopy() {
        const code = this.share?.dataset.code;
        if (!code) return;
        const url = this.shareUrl(code);
        const copied = await this.copyText(url);
        if (!copied) return;

        const button = this.copyButton;
        const original = button.dataset.originalText || button.textContent;
        button.dataset.originalText = original;
        button.textContent = button.dataset.copiedText || original;
        button.classList.add('cosmogen-certificate__share-button--done');
        clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => {
          button.textContent = original;
          button.classList.remove('cosmogen-certificate__share-button--done');
        }, 2000);
        window.cosmogenAnalytics?.track('certificate_share', { method: 'copy' });
      }

      async copyText(text) {
        try {
          if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
          }
        } catch (error) {
          /* переходим к запасному способу */
        }
        try {
          const area = document.createElement('textarea');
          area.value = text;
          area.setAttribute('readonly', '');
          area.style.position = 'fixed';
          area.style.opacity = '0';
          document.body.appendChild(area);
          area.select();
          const ok = document.execCommand('copy');
          document.body.removeChild(area);
          return ok;
        } catch (error) {
          return false;
        }
      }

      async onShare() {
        const code = this.share?.dataset.code;
        if (!code || !navigator.share) return;

        const url = this.shareUrl(code);
        const title = `${this.dataset.shareText || 'CosmoGen Diamonds certificate'} ${code}`;
        const data = { title, text: title, url };

        // На телефонах делимся самим PDF, если он уже скачан и система умеет файлы
        if (this.lastPdf && this.lastPdf.code === code && navigator.canShare) {
          const file = new File([this.lastPdf.blob], `CosmoGen_Certificate_${code}.pdf`, { type: 'application/pdf' });
          if (navigator.canShare({ files: [file] })) data.files = [file];
        }

        try {
          await navigator.share(data);
          window.cosmogenAnalytics?.track('certificate_share', { method: data.files ? 'file' : 'link' });
        } catch (error) {
          if (error.name === 'AbortError') return;
          // Файл не принят — пробуем только ссылку
          if (data.files) {
            try {
              await navigator.share({ title, text: title, url });
            } catch (retryError) {
              /* пользователь отменил или система не поддерживает */
            }
          }
        }
      }

      showError(message) {
        if (message && this.errorMessage) {
          this.errorMessage.textContent = message;
        }
        this.errorContainer.hidden = false;
        this.codeInput.setAttribute('aria-invalid', 'true');
        this.codeInput.setAttribute('aria-describedby', 'CosmogenCertificateForm-error');
      }

      hideError() {
        this.errorContainer.hidden = true;
        this.codeInput.removeAttribute('aria-invalid');
        this.codeInput.removeAttribute('aria-describedby');
      }

      setLoading(isLoading) {
        this.submitButton.disabled = isLoading;
        this.buttonText.hidden = isLoading;
        this.buttonLoading.hidden = !isLoading;
      }
    }
  );
}
